<script setup lang="ts">
import { ref, computed } from 'vue';

interface MenuItem { name: string; link: string; subtitle?: string; action?: string }
interface AboutMenu { name: string; submenu: MenuItem[] }
interface LayoutData {
  isAuthenticated: boolean;
  drawer: boolean | null;
  mainmenu: MenuItem[];
  aboutmenu: AboutMenu;
  adminmenu?: MenuItem[];
}

const props = defineProps<{ layoutData: LayoutData }>();

const drawer = ref<boolean>(props.layoutData.drawer === true);
const mainmenu = props.layoutData.mainmenu;
const aboutmenu = props.layoutData.aboutmenu;
const adminmenu = props.layoutData.adminmenu ?? [];
const isAuth = props.layoutData.isAuthenticated;

// Дедупликация: adminmenu частично повторяет mainmenu (РАСПИСАНИЕ, НОВОСТИ, …).
// Раньше оба списка рендерились одновременно → пункты дублировались в боковом
// меню. Фильтруем из mainmenu всё, что уже есть в adminmenu по нормализованному
// link/action, чтобы каждый пункт появлялся ровно один раз.
function menuKey(item: MenuItem): string {
  if (item.action) return `action:${item.action}`;
  return `link:${(item.link || '').replace(/\/+$/, '').toLowerCase()}`;
}
const adminmenuKeys = computed(() => new Set(adminmenu.map(menuKey)));
const dedupedMainmenu = computed(() =>
  mainmenu.filter((item) => !adminmenuKeys.value.has(menuKey(item)))
);

// Текущий путь (без trailing slash, в нижнем регистре) — для подсветки активного
// пункта меню жёлтой полосой, как в классическом #slide-out (.is-current из JSTopInit).
const currentPath = (typeof window !== 'undefined' && window.location
  ? window.location.pathname.replace(/\/+$/, '').toLowerCase()
  : '');
// Сегментно-граничное сопоставление: '/Courses' не должно ловить '/CoursesInOtherCities'.
function isActive(link?: string): boolean {
  if (!link) return false;
  const l = link.replace(/\/+$/, '').toLowerCase();
  if (l === '') return currentPath === '';
  return currentPath === l || currentPath.startsWith(l + '/');
}

function logout(ev: Event) {
  if (ev && typeof ev.preventDefault === 'function') ev.preventDefault();
  (window as unknown as { submitLogoutForm: () => void }).submitLogoutForm();
}

function navClick(item: MenuItem, ev: MouseEvent) {
  if (item.action === 'logout') {
    logout(ev);
  }
}
</script>

<template>
  <v-app id="inspire">
    <v-app-bar
      color="#569856"
      :elevation="2"
      :height="120"
      class="app-bar"
    >
      <div class="app-bar-inner">
        <div class="app-bar-left">
          <v-app-bar-nav-icon
            color="white"
            v-on:click="drawer = !drawer"
          ></v-app-bar-nav-icon>
        </div>

        <div class="app-bar-center">
          <a href="/" class="logo-link">
            <v-img src="/dist/Content/img/logo.svg" width="120" height="120" />
          </a>
        </div>

        <div class="app-bar-right">
          <div class="app-bar-phones">
            <a href="tel:+74956811777" class="top-phone">+7 495 681 1777</a>
            <a href="tel:+79661128019" class="top-phone">+7 966 112 9819</a>
          </div>
          <v-btn
            href="/SchedulesToday"
            color="black"
            variant="flat"
            class="schedule-btn"
          >
            РАСПИСАНИЕ
          </v-btn>
          <div class="app-bar-social">
            <a href="#" class="social-icon"><v-img src="/dist/Content/img/fb.svg" width="28" height="28" /></a>
            <a href="#" class="social-icon"><v-img src="/dist/Content/img/vk.svg" width="28" height="28" /></a>
            <a href="#" class="social-icon"><v-img src="/dist/Content/img/insta.svg" width="28" height="28" /></a>
          </div>
        </div>
      </div>
    </v-app-bar>

    <v-navigation-drawer
      v-model="drawer"
      :width="340"
      color="black"
      theme="dark"
      class="app-drawer"
    >
      <v-list density="compact" class="pa-1">
        <v-list-item>
          <template #prepend>
            <v-icon color="white">mdi-home</v-icon>
          </template>
          <template #append>
            <v-btn icon v-on:click.stop="drawer = !drawer">
              <v-icon color="white">mdi-arrow-left</v-icon>
            </v-btn>
          </template>
        </v-list-item>
      </v-list>

      <v-list class="pt-0" nav>
        <v-list-group value="about">
          <template #activator="{ props: activatorProps }">
            <v-list-item v-bind="activatorProps" :title="aboutmenu.name"></v-list-item>
          </template>
          <v-list-item
            v-for="(item, i) in aboutmenu.submenu"
            :key="`about-${i}`"
            :href="item.link"
            :active="isActive(item.link)"
            :title="item.name"
          />
        </v-list-group>

        <v-list-item
          v-for="item in dedupedMainmenu"
          :key="item.name"
          :href="item.link"
          :active="isActive(item.link)"
          :title="item.name"
        >
          <template v-if="item.subtitle" #subtitle>
            <span style="font-size: .8em; line-height: .8em;">{{ item.subtitle }}</span>
          </template>
        </v-list-item>
      </v-list>

      <v-divider v-if="isAuth"></v-divider>

      <v-list v-if="isAuth" class="pt-0 app-drawer-admin-list" nav>
        <v-list-item
          v-for="item in adminmenu"
          :key="item.name"
          :href="item.action === 'logout' ? undefined : item.link"
          :active="item.action !== 'logout' && isActive(item.link)"
          v-on:click="navClick(item, $event)"
          :title="item.name"
        >
          <template v-if="item.subtitle" #subtitle>
            <span style="font-size: .8em; line-height: .8em;">{{ item.subtitle }}</span>
          </template>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>
  </v-app>
</template>

<style>
.app-bar :deep(.v-toolbar__content) {
  height: 120px !important;
  padding: 0;
}
.app-bar-inner {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: center;
  width: 100%;
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 16px;
  height: 120px;
}
.app-bar-left { justify-self: start; }
.app-bar-center { justify-self: center; }
.app-bar-right {
  justify-self: end;
  display: flex;
  align-items: center;
  gap: 16px;
}
.app-bar-phones {
  display: flex;
  flex-direction: column;
  font-size: 14px;
}
.top-phone {
  color: white;
  text-decoration: none;
  font-weight: bold;
}
.schedule-btn {
  text-transform: none;
  letter-spacing: 0;
  font-weight: bold;
}
.app-bar-social {
  display: flex;
  gap: 4px;
}
.social-icon { display: inline-flex; }
/* ── Боковое меню: VueV3-drawer в стиле классического #slide-out (TopMenu.scss) ──
   Чёрный фон, тонкий шрифт (font-weight 200 как .font-reset), жёлтый #F2DD5A
   hover/active с тонкой подложкой + левая полоса 3px у активного пункта,
   мягкие разделители, тёмное подменю «О СТУДИИ», тонкий скроллбар. */
/* Размер текста задаётся на корневом контейнере, чтобы ВСЕ списки drawer-а
   (main, submenu «О СТУДИИ», admin) наследовали единый размер и не
   рассыпались по разным величинам. Раньше переменная висела на .v-list-item,
   из-за чего admin-список приходилось подтягивать отдельным !important. */
.app-drawer {
  --app-drawer-menu-font-size: 1.4em;
}
.app-drawer .v-list-item {
  font-weight: 200;
  letter-spacing: .02em;
  transition: background-color .18s ease, color .18s ease;
}
.app-drawer .v-list-item .v-list-item__title {
  color: #ffffff;
  font-size: var(--app-drawer-menu-font-size);
}
/* Перекрашиваем Vuetify-оверлей (hover/active фон) из белого currentColor в
   фирменный жёлтый — Vuetify сам варьирует opacity на hover/active. */
.app-drawer .v-list-item__overlay { background-color: #f2dd5a !important; }
.app-drawer .v-list-item:hover .v-list-item__title { color: #f2dd5a; }
/* Активный/текущий пункт: жёлтый текст + жирность 500 + левая полоса 3px,
   как .is-current у классического sidenav (inset-bar на самом оверлее,
   чтобы он был выше фонового слоя). */
.app-drawer .v-list-item--active .v-list-item__overlay {
  box-shadow: inset 3px 0 0 #f2dd5a;
}
.app-drawer .v-list-item--active .v-list-item__title {
  color: #f2dd5a;
  font-weight: 500;
}
/* Мягкие разделители между пунктами (как li border-bottom в #slide-out). */
.app-drawer .v-list--nav .v-list-item:not(:last-child) {
  border-bottom: 1px solid rgba(255, 255, 255, 0.06);
}
/* Подменю «О СТУДИИ»: тёмный фон, отступ, мельче. */
.app-drawer .v-list-group__items { background-color: rgba(0, 0, 0, 0.25); }
.app-drawer .v-list-group__items .v-list-item { padding-inline-start: 2.4em !important; }
.app-drawer .v-list-group__items .v-list-item .v-list-item__title {
  color: rgba(255, 255, 255, 0.85);
  font-size: var(--app-drawer-menu-font-size);
}
/* Тонкий скроллбар как у #slide-out. */
.app-drawer::-webkit-scrollbar { width: 6px; }
.app-drawer::-webkit-scrollbar-track { background: rgba(255, 255, 255, 0.04); }
.app-drawer::-webkit-scrollbar-thumb { background: rgba(255, 255, 255, 0.18); border-radius: 3px; }
.app-drawer::-webkit-scrollbar-thumb:hover { background: rgba(255, 255, 255, 0.3); }
</style>
